'use client';

import { Button, FormField, H1, SchemaForm, Text, XStack, YStack, useForm } from '@cxnpl/ui';
import type { UseFormReturn } from 'react-hook-form';
import { z } from 'zod';
import { useTransition } from 'react';
import { useTranslation } from 'react-i18next';
import { useCountdown } from 'app/hooks/useCountdown';
import i18n from 'app/i18n/i18n.config';
import { withTrackScreenView } from 'app/analytics';

export const MfaFormSchema = z.object({
  code: FormField.PasswordCode((zString) =>
    zString({
      required_error: `${i18n.t('auth.components.mfaForm.securityCodeRequiredErrorMessage')}`,
    })
      .min(1, `${i18n.t('auth.components.mfaForm.securityCodeRequiredErrorMessage')}`)
      .max(6)
      .regex(/^[0-9]{6}$/, `${i18n.t('auth.components.mfaForm.invalidSecurityCodeErrorMessage')}`)
  ).describe(`${i18n.t('auth.components.mfaForm.securityCodeInputLabel')}`),
});

export type MfaFormFields = z.infer<typeof MfaFormSchema>;

export interface MfaFormProps {
  onSubmit: (values: MfaFormFields, form: UseFormReturn<MfaFormFields>) => Promise<void> | void;
  onResend?: (form: UseFormReturn<MfaFormFields>) => Promise<void> | void;
  email?: string;
  mobile?: string;
}

export function MfaForm({ onSubmit: onSubmitCallback, onResend, email, mobile }: MfaFormProps) {
  const { t } = useTranslation();
  const { startCountdown, countdown } = useCountdown();
  const [isPending, startTransition] = useTransition();

  const form = useForm(MfaFormSchema, {
    reValidateMode: 'onSubmit',
  });
  const { isSubmitting } = form.formState;
  const isSubmittingForm = isSubmitting || isPending;

  const onSubmit = (values: MfaFormFields) => {
    startTransition(async () => {
      await onSubmitCallback(values, form);
    });
  };

  const submit = () => {
    void form.handleSubmit(onSubmit)();
  };

  let descriptionText;

  switch (true) {
    case !!email:
      descriptionText = t('auth.components.mfaForm.emailDescription', { email });
      break;
    case !!mobile:
      descriptionText = t('auth.components.mfaForm.mobileDescription', { mobile });
      break;
    default:
      descriptionText = t('auth.components.mfaForm.description');
  }

  return (
    <YStack gap="$space.2xl">
      <YStack gap="$space.2xl">
        <H1 color="$foreground/surface-default">{t('auth.components.mfaForm.title')}</H1>
        <Text variant="bodyMedium" flexWrap="wrap" color="$foreground/surface-subdued">
          {descriptionText}
        </Text>
      </YStack>
      <SchemaForm
        form={form}
        onSubmit={onSubmit}
        schema={MfaFormSchema}
        formProps={{
          disabled: isSubmittingForm,
        }}
        props={{
          code: {
            isNumericOnly: true,
            focusAfterSubmit: true,
            autoFocus: true,
            alwaysFocused: false,
            autoSubmit: submit,
            onChangeText: () => {
              form.clearErrors();
            },
          },
        }}
      >
        {(fields) => (
          <YStack gap="$space.lg">
            {fields.code}
            {onResend ? (
              <YStack>
                {countdown === 0 ? (
                  <Text
                    variant="linkMedium"
                    width="max-content"
                    link
                    color="$button/color/button-primary-text"
                    onPress={() => {
                      startCountdown(30);
                      void onResend(form);
                    }}
                    disabled={isSubmittingForm}
                  >
                    {t('auth.components.mfaForm.resendLink')}
                  </Text>
                ) : (
                  <XStack gap={4}>
                    <Text color="$foreground/surface-subdued">{t('auth.components.mfaForm.resendCountdownText')}</Text>
                    <Text color="$foreground/surface-default">{countdown}</Text>
                  </XStack>
                )}
              </YStack>
            ) : null}
          </YStack>
        )}
      </SchemaForm>
      <YStack alignItems="flex-start">
        <Button
          mode="primary"
          variant="filled"
          size="lg"
          onPress={submit}
          loading={isSubmittingForm}
          disabled={isSubmittingForm}
        >
          {t('auth.components.mfaForm.confirmButtonLabel')}
        </Button>
      </YStack>
    </YStack>
  );
}

export function MfaFormWithPageName(pageName: string) {
  return withTrackScreenView(MfaForm, {
    pageName,
  });
}
